import type {
  ConditionAnalysisBucketRow,
  ConditionAnalysisSituationRow,
  ConditionAnalysisSlice,
  ConditionAnalysisSummaryRow,
  ConditionAnalysisScope,
} from "./conditionAnalysisSelectors";
import {
  parseCandleDirectionTradeDirectionKey,
  parseRegimeDirectionKey,
  parseRegimeDirectionTradeDirectionKey,
  parseSessionTradeDirectionKey,
} from "./conditionAnalysisSelectors";
import type { ManagementExplorerSliceRow } from "../types/managementExplorer";
import type { ReportPickSliceContext } from "./reportPickListStorage";
import { regimeDrilldownTableKeyFromBreakdownMap } from "./marketContextDrilldownLabels";
import { buildExplorerSliceId } from "./managementExplorer";
import { resolveCohortTrades } from "../services/reportsApi";

export type CohortTradesResolveSource = "condition_analysis" | "management_explorer";

export type CohortTradesResolveTarget =
  | { type: "bucket"; bucket: string; segment_key?: string; policy_import_nodes?: unknown[] }
  | {
      type: "situation";
      situation: string;
      clauses?: Array<{ key: string; value: string }>;
      policy_import_nodes?: unknown[];
    }
  | { type: "slice"; slice_id: string; policy_import_nodes?: unknown[] }
  | {
      type: "segment";
      key?: string;
      segment_key?: string;
      policy_import_nodes?: unknown[];
    };

export type CohortTradesApiSliceContext = {
  slice_id?: string;
  kind?: string;
  label?: string;
  segment_key?: string;
  market_condition?: string;
  candle_direction?: string;
  trade_direction?: "long" | "short";
  triple_candle_window?: "long" | "short";
  session_id?: string;
};

export type CohortTradesResolveRequestBody = {
  source: CohortTradesResolveSource;
  slice_context?: CohortTradesApiSliceContext | null;
  target: CohortTradesResolveTarget;
};

export type CohortTradesResolvePagination = {
  offset: number;
  limit: number;
  has_more: boolean;
  total: number;
};

export type CohortTradesResolveResponse = {
  schema_version?: string;
  report_id?: string;
  source?: CohortTradesResolveSource;
  target?: CohortTradesResolveTarget;
  slice_context?: CohortTradesApiSliceContext;
  trade_count?: number;
  trade_ids: string[];
  pagination?: CohortTradesResolvePagination;
};

export type CohortListFilter = "all" | "cohort" | "winners" | "losers";

export type CohortOutcome = "win" | "loss" | "breakeven";

/** Cohort preview win/loss — used in lists; chart labels use direction colors instead. */
export const COHORT_PREVIEW_WIN_COLOR = "#38BBD1";
export const COHORT_PREVIEW_LOSS_COLOR = "#F49113";
export const COHORT_PREVIEW_WIN_RGBA = "rgba(56, 187, 209, 0.85)";
export const COHORT_PREVIEW_LOSS_RGBA = "rgba(244, 145, 19, 0.85)";
export const COHORT_PREVIEW_OTHER_COLOR = "#6B7280";
export const COHORT_PREVIEW_OTHER_RGBA = "rgba(107, 114, 128, 0.4)";

/** Chart label colors — long = green, short = red (outcome is in the text). */
export const COHORT_PREVIEW_LONG_COLOR = "#06E363";
export const COHORT_PREVIEW_SHORT_COLOR = "#E00101";
export const COHORT_PREVIEW_LONG_RGBA = "rgba(6, 227, 99, 0.92)";
export const COHORT_PREVIEW_SHORT_RGBA = "rgba(224, 1, 1, 0.92)";

export function cohortPreviewMarkerShape(
  direction: string | null | undefined,
): "arrowUp" | "arrowDown" {
  return direction === "short" ? "arrowDown" : "arrowUp";
}

export function cohortPreviewMarkerPosition(
  direction: string | null | undefined,
): "aboveBar" | "belowBar" {
  return direction === "short" ? "belowBar" : "aboveBar";
}

/** @deprecated Prefer {@link cohortPreviewOutcomeTextColor} for outcome labels. */
export function cohortPreviewMarkerColor(outcome: CohortOutcome | undefined): string {
  return outcome === "loss" ? COHORT_PREVIEW_LOSS_RGBA : COHORT_PREVIEW_WIN_RGBA;
}

/** Arrow fill — long green / short red (independent of win/loss label color). */
export function cohortPreviewDirectionColor(direction: string | null | undefined): string {
  return direction === "short" ? COHORT_PREVIEW_SHORT_RGBA : COHORT_PREVIEW_LONG_RGBA;
}

/** Label text — win green / loss red (independent of arrow direction color). */
export function cohortPreviewOutcomeTextColor(outcome: CohortOutcome | undefined): string {
  return outcome === "loss" ? COHORT_PREVIEW_SHORT_RGBA : COHORT_PREVIEW_LONG_RGBA;
}

export function cohortPreviewOutcomeLetter(outcome: CohortOutcome | undefined): "Win" | "Loss" {
  return outcome === "loss" ? "Loss" : "Win";
}

/** e.g. Long Win, Short Loss — count suffix when stacked on one candle. */
export function cohortPreviewOutcomeLabel(
  direction: string | null | undefined,
  outcome: CohortOutcome | undefined,
  count = 1,
): string {
  const side = direction === "short" ? "Short" : "Long";
  const result = cohortPreviewOutcomeLetter(outcome);
  const base = `${side} ${result}`;
  return count > 1 ? `${base}×${count}` : base;
}

export function formatCohortSideLabel(parts: Array<{ label: string; count: number }>): string {
  return parts
    .filter((p) => p.count > 0)
    .map((p) => (p.count > 1 ? `${p.label}×${p.count}` : p.label))
    .join(" · ");
}

const DEFAULT_PAGE_LIMIT = 500;

/** @deprecated Root tables send `slice_context: null` — not `{ kind: "all" }`. */
export const ROOT_SLICE_CONTEXT: CohortTradesApiSliceContext = {
  slice_id: "all",
  kind: "all",
};

/** Row fields used to derive slice scope and policy import nodes for cohort resolve. */
export type CohortPreviewRowFields = {
  segment_context?: string;
  universe?: string;
  segment_key?: string;
  drill_key?: string;
  kind?: string;
  bucket?: string;
  situation?: string;
  /** Display-only label from snapshot — never send as bucket/situation target. */
  label?: string;
  policy_import_nodes?: unknown;
  simulator_suggestions?: unknown;
  simulator_suggestion_nodes?: unknown;
  simulator_suggestion_node_for_metric_bucket?: unknown;
  simulator_suggestion_node_for_situation?: unknown;
  simulator_suggestion_node?: unknown;
};

export function extractPolicyImportNodes(
  row: CohortPreviewRowFields | null | undefined
): unknown[] | undefined {
  if (!row) return undefined;
  const raw =
    row.policy_import_nodes ??
    row.simulator_suggestions ??
    row.simulator_suggestion_nodes ??
    row.simulator_suggestion_node_for_metric_bucket ??
    row.simulator_suggestion_node_for_situation ??
    row.simulator_suggestion_node ??
    undefined;
  if (Array.isArray(raw)) return raw.length ? raw : undefined;
  if (raw && typeof raw === "object") return [raw];
  return undefined;
}

function normalizeRegimeValue(value: string | undefined): string | undefined {
  if (!value?.trim()) return undefined;
  const v = value.trim().toLowerCase();
  if (v === "trending") return "trend";
  if (v === "ranging" || v === "range_bound") return "range";
  return v;
}

function normalizeCandleDirection(value: string | undefined): string | undefined {
  if (!value?.trim()) return undefined;
  const v = value.trim().toLowerCase();
  if (v === "bullish") return "up";
  if (v === "bearish") return "down";
  return v;
}

/** Map segment_context / universe segment token to a fully scoped BE slice_context. */
export function segmentContextToSliceContext(
  segmentContext: string
): CohortTradesApiSliceContext | null {
  const raw = String(segmentContext ?? "").trim();
  if (!raw) return null;

  if (raw === "long" || raw === "short") {
    return {
      slice_id: "trade_direction",
      kind: "trade_direction",
      trade_direction: raw,
    };
  }

  const candleTrade = parseCandleDirectionTradeDirectionKey(raw);
  if (candleTrade) {
    return {
      slice_id: "candle_direction_and_trade_direction",
      kind: "candle_direction_and_trade_direction",
      segment_key: raw,
      candle_direction:
        normalizeCandleDirection(candleTrade.candle_direction) ?? candleTrade.candle_direction,
      trade_direction: candleTrade.trade_direction,
    };
  }

  const regimeDirection = parseRegimeDirectionKey(raw);
  if (regimeDirection) {
    return {
      slice_id: "market_condition_and_direction",
      kind: "market_condition_and_direction",
      segment_key: raw,
      market_condition:
        normalizeRegimeValue(regimeDirection.market_condition) ?? regimeDirection.market_condition,
      candle_direction:
        normalizeCandleDirection(regimeDirection.candle_direction) ??
        regimeDirection.candle_direction,
    };
  }

  const sessionTrade = parseSessionTradeDirectionKey(raw);
  if (sessionTrade) {
    return {
      slice_id: "session_and_trade_direction",
      kind: "session_and_trade_direction",
      segment_key: raw,
      session_id: sessionTrade.session_id,
      trade_direction: sessionTrade.trade_direction,
    };
  }

  const triple = parseRegimeDirectionTradeDirectionKey(raw);
  if (triple) {
    return {
      slice_id: "market_condition_and_direction_and_trade_direction",
      kind: "market_condition_and_direction_and_trade_direction",
      segment_key: raw,
      market_condition: normalizeRegimeValue(triple.market_condition) ?? triple.market_condition,
      candle_direction: normalizeCandleDirection(triple.candle_direction) ?? triple.candle_direction,
      trade_direction: triple.trade_direction,
      triple_candle_window: "long",
    };
  }

  const single = raw.toLowerCase();
  const regime = normalizeRegimeValue(single);
  if (regime) {
    return {
      slice_id: "market_condition",
      kind: "market_condition",
      market_condition: regime,
    };
  }
  const candle = normalizeCandleDirection(single);
  if (candle) {
    return {
      slice_id: "candle_direction",
      kind: "candle_direction",
      candle_direction: candle,
    };
  }

  return null;
}

function mapReportPickSliceContextForResolve(
  ctx: ReportPickSliceContext
): CohortTradesApiSliceContext | null {
  switch (ctx.kind) {
    case "regime":
      if (!ctx.market_condition?.trim()) return null;
      return {
        slice_id: "market_condition",
        kind: "market_condition",
        market_condition: normalizeRegimeValue(ctx.market_condition) ?? ctx.market_condition,
      };
    case "session_regime":
      if (!ctx.market_condition?.trim() || !ctx.session_id?.trim()) return null;
      return {
        slice_id: "session_and_market_condition",
        kind: "session_and_market_condition",
        session_id: ctx.session_id.trim(),
        market_condition: normalizeRegimeValue(ctx.market_condition) ?? ctx.market_condition,
      };
    case "direction":
    case "session_trade_direction":
      if (ctx.trade_direction !== "long" && ctx.trade_direction !== "short") return null;
      return {
        slice_id:
          ctx.kind === "session_trade_direction" ? "session_and_trade_direction" : "trade_direction",
        kind:
          ctx.kind === "session_trade_direction" ? "session_and_trade_direction" : "trade_direction",
        trade_direction: ctx.trade_direction,
        ...(ctx.session_id?.trim() ? { session_id: ctx.session_id.trim() } : {}),
      };
    case "market_direction":
    case "candle_direction":
    case "session_candle_direction":
      if (!ctx.candle_direction?.trim()) return null;
      return {
        slice_id: "candle_direction",
        kind: "candle_direction",
        candle_direction: normalizeCandleDirection(ctx.candle_direction) ?? ctx.candle_direction,
        ...(ctx.session_id?.trim() ? { session_id: ctx.session_id.trim() } : {}),
      };
    case "market_direction_short":
    case "candle_direction_short":
    case "session_candle_direction_short":
      if (!ctx.candle_direction?.trim()) return null;
      return {
        slice_id: "candle_direction_short",
        kind: "candle_direction_short",
        candle_direction: normalizeCandleDirection(ctx.candle_direction) ?? ctx.candle_direction,
        ...(ctx.session_id?.trim() ? { session_id: ctx.session_id.trim() } : {}),
      };
    case "regime_direction":
    case "session_regime_trade_direction":
      if (!ctx.market_condition?.trim() || !ctx.candle_direction?.trim()) return null;
      return {
        slice_id:
          ctx.kind === "session_regime_trade_direction"
            ? "session_and_market_condition_and_trade_direction"
            : "market_condition_and_direction",
        kind:
          ctx.kind === "session_regime_trade_direction"
            ? "session_and_market_condition_and_trade_direction"
            : "market_condition_and_direction",
        market_condition: normalizeRegimeValue(ctx.market_condition) ?? ctx.market_condition,
        candle_direction: normalizeCandleDirection(ctx.candle_direction) ?? ctx.candle_direction,
        ...(ctx.trade_direction ? { trade_direction: ctx.trade_direction } : {}),
        ...(ctx.session_id?.trim() ? { session_id: ctx.session_id.trim() } : {}),
      };
    case "candle_direction_trade_direction":
      if (!ctx.candle_direction?.trim() || !ctx.trade_direction) return null;
      return {
        slice_id: "candle_direction_and_trade_direction",
        kind: "candle_direction_and_trade_direction",
        candle_direction: normalizeCandleDirection(ctx.candle_direction) ?? ctx.candle_direction,
        trade_direction: ctx.trade_direction,
      };
    case "candle_direction_short_trade_direction":
      if (!ctx.candle_direction?.trim() || !ctx.trade_direction) return null;
      return {
        slice_id: "candle_direction_short_and_trade_direction",
        kind: "candle_direction_short_and_trade_direction",
        candle_direction: normalizeCandleDirection(ctx.candle_direction) ?? ctx.candle_direction,
        trade_direction: ctx.trade_direction,
      };
    case "regime_direction_trade_direction":
    case "session_regime_direction_trade_direction":
    case "session_regime_direction_short_trade_direction":
      if (!ctx.market_condition?.trim() || !ctx.candle_direction?.trim() || !ctx.trade_direction) {
        return null;
      }
      return {
        slice_id:
          ctx.kind === "session_regime_direction_short_trade_direction"
            ? "session_and_market_condition_and_direction_short_and_trade_direction"
            : ctx.kind === "session_regime_direction_trade_direction"
              ? "session_and_market_condition_and_direction_and_trade_direction"
              : "regime_direction_trade_direction",
        kind:
          ctx.kind === "session_regime_direction_short_trade_direction"
            ? "session_and_market_condition_and_direction_short_and_trade_direction"
            : ctx.kind === "session_regime_direction_trade_direction"
              ? "session_and_market_condition_and_direction_and_trade_direction"
              : "regime_direction_trade_direction",
        market_condition: normalizeRegimeValue(ctx.market_condition) ?? ctx.market_condition,
        candle_direction: normalizeCandleDirection(ctx.candle_direction) ?? ctx.candle_direction,
        trade_direction: ctx.trade_direction,
        triple_candle_window:
          ctx.triple_candle_window ??
          (ctx.kind === "session_regime_direction_short_trade_direction" ? "short" : "long"),
        ...(ctx.session_id?.trim() ? { session_id: ctx.session_id.trim() } : {}),
        ...(ctx.label?.trim() ? { label: ctx.label.trim() } : {}),
      };
    case "session_time":
      if (!ctx.session_id?.trim()) return null;
      return {
        slice_id: "session",
        kind: "session",
        session_id: ctx.session_id.trim(),
      };
    default:
      return null;
  }
}

/** Map FE slice context to BE resolve payload (canonical slice_id/kind + enums). */
export function mapSliceContextForResolve(
  ctx: ReportPickSliceContext | null | undefined
): CohortTradesApiSliceContext | null {
  if (!ctx || ctx.kind === "all") {
    return null;
  }
  return mapReportPickSliceContextForResolve(ctx);
}

/** Build slice_context from a nested node's `analysis_scope` (regime_breakdown drill-down). */
export function sliceContextFromAnalysisScope(
  scope: ConditionAnalysisScope | null | undefined
): CohortTradesApiSliceContext | null {
  const path = String(scope?.path ?? "").trim();
  const segmentKey = String(scope?.segment_key ?? "").trim();
  if (!path || path === "condition_analysis" || !segmentKey) return null;

  const breakdownMatch = path.match(/^regime_breakdown\.(.+)$/);
  if (!breakdownMatch) return null;
  const mapKey = breakdownMatch[1];

  switch (mapKey) {
    case "by_trade_direction":
      if (segmentKey !== "long" && segmentKey !== "short") return null;
      return {
        slice_id: "trade_direction",
        kind: "trade_direction",
        trade_direction: segmentKey,
      };
    case "by_market_condition":
      return {
        slice_id: "market_condition",
        kind: "market_condition",
        market_condition: normalizeRegimeValue(segmentKey) ?? segmentKey,
      };
    case "by_candle_direction":
      return {
        slice_id: "candle_direction",
        kind: "candle_direction",
        candle_direction: normalizeCandleDirection(segmentKey) ?? segmentKey,
      };
    case "by_candle_direction_short":
    case "by_market_direction_short":
      return {
        slice_id: "candle_direction_short",
        kind: "candle_direction_short",
        candle_direction: normalizeCandleDirection(segmentKey) ?? segmentKey,
        triple_candle_window: "short",
      };
    case "by_market_direction":
      return {
        slice_id: "candle_direction",
        kind: "candle_direction",
        candle_direction: normalizeCandleDirection(segmentKey) ?? segmentKey,
      };
    case "by_session":
      return {
        slice_id: "session",
        kind: "session",
        session_id: segmentKey,
      };
    case "by_candle_direction_and_trade_direction": {
      const p = parseCandleDirectionTradeDirectionKey(segmentKey);
      if (!p) return null;
      return {
        slice_id: "candle_direction_and_trade_direction",
        kind: "candle_direction_and_trade_direction",
        segment_key: segmentKey,
        candle_direction: normalizeCandleDirection(p.candle_direction) ?? p.candle_direction,
        trade_direction: p.trade_direction,
      };
    }
    case "by_candle_direction_short_and_trade_direction": {
      const p = parseCandleDirectionTradeDirectionKey(segmentKey);
      if (!p) return null;
      return {
        slice_id: "candle_direction_short_and_trade_direction",
        kind: "candle_direction_short_and_trade_direction",
        segment_key: segmentKey,
        candle_direction: normalizeCandleDirection(p.candle_direction) ?? p.candle_direction,
        trade_direction: p.trade_direction,
        triple_candle_window: "short",
      };
    }
    case "by_market_condition_and_direction_and_trade_direction": {
      const p = parseRegimeDirectionTradeDirectionKey(segmentKey);
      if (!p) return null;
      return {
        slice_id: "market_condition_and_direction_and_trade_direction",
        kind: "market_condition_and_direction_and_trade_direction",
        segment_key: segmentKey,
        market_condition: normalizeRegimeValue(p.market_condition) ?? p.market_condition,
        candle_direction: normalizeCandleDirection(p.candle_direction) ?? p.candle_direction,
        trade_direction: p.trade_direction,
        triple_candle_window: "long",
      };
    }
    case "by_market_condition_and_direction_short_and_trade_direction": {
      const p = parseRegimeDirectionTradeDirectionKey(segmentKey);
      if (!p) return null;
      return {
        slice_id: "market_condition_and_direction_short_and_trade_direction",
        kind: "market_condition_and_direction_short_and_trade_direction",
        segment_key: segmentKey,
        market_condition: normalizeRegimeValue(p.market_condition) ?? p.market_condition,
        candle_direction: normalizeCandleDirection(p.candle_direction) ?? p.candle_direction,
        trade_direction: p.trade_direction,
        triple_candle_window: "short",
      };
    }
    default:
      if (mapKey.startsWith("by_session_and_")) {
        return {
          slice_id: mapKey.replace(/^by_/, ""),
          kind: mapKey.replace(/^by_/, ""),
          segment_key: segmentKey,
        };
      }
      return null;
  }
}

/** Resolve slice_context for a preview row — row segment_context/universe override active tab. */
export function resolveSliceContextForCohortPreview(
  row: CohortPreviewRowFields | null | undefined,
  activeSlice: ReportPickSliceContext | null | undefined,
  activeAnalysisScope?: ConditionAnalysisScope | null
): CohortTradesApiSliceContext | null {
  const segmentContext = String(row?.segment_context ?? "").trim();
  if (segmentContext) {
    const fromRow = segmentContextToSliceContext(segmentContext);
    if (fromRow) return fromRow;
  }

  const universe = String(row?.universe ?? "").trim();
  if (universe === "all_closed_trades") return null;

  if (universe.startsWith("segment:")) {
    const fromUniverse = segmentContextToSliceContext(universe.replace(/^segment:/, ""));
    if (fromUniverse) return fromUniverse;
  }

  const fromScope = sliceContextFromAnalysisScope(activeAnalysisScope);
  if (fromScope) return fromScope;

  if (!activeSlice || activeSlice.kind === "all") return null;

  return mapReportPickSliceContextForResolve(activeSlice);
}

function situationPipeFromClauses(
  clauses: Array<{ key?: string; value?: string }>
): string {
  return clauses
    .map((c) => `${String(c.key ?? "").trim()}=${String(c.value ?? "").trim()}`)
    .filter((x) => x !== "=")
    .sort()
    .join("|");
}

/** True when resolve target should be `situation` (not `bucket`). */
export function isCohortSituationRow(row: CohortPreviewRowFields): boolean {
  if (String(row.kind ?? "").trim().toLowerCase() === "situation") return true;
  return Boolean(String(row.situation ?? "").trim());
}

/** Canonical bucket token — never uses display `label`. */
export function canonicalBucketTokenFromRow(row: CohortPreviewRowFields): string {
  const bucket = String(row.bucket ?? "").trim();
  if (bucket) return bucket;
  if (!isCohortSituationRow(row)) {
    return String(row.situation ?? "").trim();
  }
  return "";
}

function situationRowSignature(row: ConditionAnalysisSituationRow): string {
  if (row.clauses?.length) {
    return situationPipeFromClauses(row.clauses);
  }
  return String(row.situation ?? "").trim();
}

/**
 * Copy `policy_import_nodes` / `simulator_suggestions` from parsed snapshot rows onto
 * lightweight rows (e.g. `regime_actionable_filters`) that omit policy hints.
 */
export function enrichSituationRowsWithPolicyNodes(
  rows: ConditionAnalysisSituationRow[],
  sourceRows: ConditionAnalysisSituationRow[]
): ConditionAnalysisSituationRow[] {
  const bySig = new Map<string, ConditionAnalysisSituationRow>();
  for (const source of sourceRows) {
    const sig = situationRowSignature(source);
    if (sig) bySig.set(sig, source);
  }
  return rows.map((row) => {
    if (extractPolicyImportNodes(row)?.length) return row;
    const match = bySig.get(situationRowSignature(row));
    if (!match) return row;
    const nodes = extractPolicyImportNodes(match);
    if (!nodes?.length) return row;
    return {
      ...row,
      policy_import_nodes: nodes,
      ...(match.simulator_suggestions?.length
        ? { simulator_suggestions: match.simulator_suggestions }
        : {}),
    };
  });
}

/**
 * Build a condition-analysis cohort resolve body from any bucket/situation snapshot row.
 * Always attaches `policy_import_nodes` when the row carries them; never uses display `label`.
 */
export function buildCohortResolveRequestFromRow(
  row: (ConditionAnalysisBucketRow | ConditionAnalysisSituationRow) & CohortPreviewRowFields,
  activeSlice: ReportPickSliceContext | null | undefined,
  activeAnalysisScope?: ConditionAnalysisScope | null
): CohortTradesResolveRequestBody {
  const policy = extractPolicyImportNodes(row);
  const slice_context = resolveSliceContextForCohortPreview(row, activeSlice, activeAnalysisScope);

  if (isCohortSituationRow(row)) {
    const situationRow = row as ConditionAnalysisSituationRow;
    const target: CohortTradesResolveTarget = {
      type: "situation",
      situation: situationTargetFromRow(situationRow),
      ...(situationRow.clauses?.length ? { clauses: situationRow.clauses } : {}),
      ...(policy ? { policy_import_nodes: policy } : {}),
    };
    return buildConditionAnalysisResolveBody(target, slice_context);
  }

  const bucket = canonicalBucketTokenFromRow(row);
  const segmentKey = String(row.segment_key ?? row.drill_key ?? "").trim();
  const target: CohortTradesResolveTarget = {
    type: "bucket",
    bucket,
    ...(segmentKey ? { segment_key: segmentKey } : {}),
    ...(policy ? { policy_import_nodes: policy } : {}),
  };
  return buildConditionAnalysisResolveBody(target, slice_context);
}

/** Build situation target pipe string for BE resolve. */
export function situationTargetFromRow(row: ConditionAnalysisSituationRow): string {
  if (row.clauses?.length) {
    return situationPipeFromClauses(row.clauses);
  }
  const raw = String(row.situation ?? "").trim();
  if (!raw || raw === "—") return "";
  return raw;
}

export function bucketTargetFromRow(
  row: ConditionAnalysisBucketRow & CohortPreviewRowFields
): CohortTradesResolveTarget {
  const bucket = canonicalBucketTokenFromRow(row);
  const segmentKey = String(row.segment_key ?? row.drill_key ?? "").trim();
  const policy = extractPolicyImportNodes(row);
  return {
    type: "bucket",
    bucket,
    ...(segmentKey ? { segment_key: segmentKey } : {}),
    ...(policy ? { policy_import_nodes: policy } : {}),
  };
}

function normalizeSummarySegmentKey(rowKey: string): string {
  return rowKey
    .split("|")
    .map((part) => {
      const token = part.trim().toLowerCase();
      return normalizeRegimeValue(token) ?? normalizeCandleDirection(token) ?? token;
    })
    .filter(Boolean)
    .join("|");
}

/** Breakdown table row key (`regime_summary`) — send `key` / `segment_key`, never `{axis}:{value}`. */
export function segmentTargetFromBreakdownRowKey(
  rowKey: string,
  tableKey?: string
): CohortTradesResolveTarget {
  const rk = String(rowKey ?? "").trim();
  const tk = String(tableKey ?? "").trim();

  if (rk.includes("|")) {
    return { type: "segment", segment_key: normalizeSummarySegmentKey(rk) };
  }

  switch (tk) {
    case "market_condition":
    case "regime":
      return { type: "segment", key: normalizeRegimeValue(rk) ?? rk.toLowerCase() };
    case "candle_direction":
    case "candle_direction_short":
    case "market_direction":
    case "market_direction_short":
      return { type: "segment", key: normalizeCandleDirection(rk) ?? rk.toLowerCase() };
    case "trade_direction":
      return { type: "segment", key: rk.toLowerCase() };
    case "session":
      return { type: "segment", segment_key: rk.toLowerCase() };
    default:
      return {
        type: "segment",
        key: normalizeSummarySegmentKey(rk) || rk.toLowerCase(),
      };
  }
}

/** @deprecated Use segmentTargetFromBreakdownRowKey — kept for callers that only handle regime rows. */
export function segmentTargetFromMarketConditionKey(rowKey: string): CohortTradesResolveTarget {
  return segmentTargetFromBreakdownRowKey(rowKey, "market_condition");
}

/** Map a regime_summary drilldown row to a cohort-trades resolve target. */
export function bucketTargetFromSummaryRow(
  tableKey: string,
  rowKey: string
): CohortTradesResolveTarget | null {
  const rk = String(rowKey ?? "").trim();
  if (!rk) return null;
  const tk = String(tableKey ?? "").trim();

  if (tk === "trade_direction" && rk !== "long" && rk !== "short") return null;

  return segmentTargetFromBreakdownRowKey(rk, tk);
}

/** Root `regime_summary` table clicks — disambiguate long vs short candle window. */
export function sliceContextForRootSummaryTable(
  tableKey: string,
  rowKey?: string
): CohortTradesApiSliceContext | null {
  const tk = String(tableKey ?? "").trim();
  const rk = String(rowKey ?? "").trim();
  const singleCandleDir =
    rk && !rk.includes("|")
      ? normalizeCandleDirection(rk) ?? rk.toLowerCase()
      : undefined;

  switch (tk) {
    case "candle_direction":
    case "market_direction":
      return singleCandleDir
        ? {
            kind: "candle_direction",
            candle_direction: singleCandleDir,
            triple_candle_window: "long",
          }
        : { triple_candle_window: "long" };
    case "candle_direction_short":
    case "market_direction_short":
      return singleCandleDir
        ? {
            kind: "candle_direction_short",
            candle_direction: singleCandleDir,
            triple_candle_window: "short",
          }
        : { triple_candle_window: "short" };
    case "candle_direction_and_trade_direction":
    case "market_condition_candle_direction_and_trade_direction":
      return { triple_candle_window: "long" };
    case "candle_direction_short_and_trade_direction":
    case "market_condition_candle_direction_short_and_trade_direction":
      return { triple_candle_window: "short" };
    default:
      return null;
  }
}

/** Slice-context for configured-slice headline preview (whole slice, not summary row). */
export function sliceContextForConditionSlice(
  slice: ConditionAnalysisSlice
): CohortTradesApiSliceContext | null {
  const cd = slice.candle_direction?.trim();
  const normalizedCd = cd ? normalizeCandleDirection(cd) ?? cd : undefined;

  switch (slice.kind) {
    case "candle_direction":
    case "market_direction":
      return normalizedCd
        ? {
            kind: "candle_direction",
            candle_direction: normalizedCd,
            triple_candle_window: "long",
          }
        : { triple_candle_window: "long" };
    case "candle_direction_short":
    case "market_direction_short":
      return normalizedCd
        ? {
            kind: "candle_direction_short",
            candle_direction: normalizedCd,
            triple_candle_window: "short",
          }
        : { triple_candle_window: "short" };
    case "candle_direction_trade_direction":
      return { triple_candle_window: "long" };
    case "candle_direction_short_trade_direction":
      return { triple_candle_window: "short" };
    case "regime_direction_trade_direction": {
      const scopePath = String(slice.node?.analysis_scope?.path ?? "");
      const isShort =
        scopePath.includes("direction_short") || slice.id.includes("short_td");
      return { triple_candle_window: isShort ? "short" : "long" };
    }
    case "session_regime_direction_short_trade_direction":
      return { triple_candle_window: "short" };
    case "session_regime_direction_trade_direction":
      return { triple_candle_window: "long" };
    default:
      return null;
  }
}

export function buildSummaryRowResolveBody(
  tableKey: string,
  row: ConditionAnalysisSummaryRow
): CohortTradesResolveRequestBody | null {
  const target = bucketTargetFromSummaryRow(tableKey, row.key);
  if (!target) return null;
  return buildConditionAnalysisResolveBody(
    target,
    sliceContextForRootSummaryTable(tableKey, row.key)
  );
}

export function buildBucketRowResolveBody(
  row: ConditionAnalysisBucketRow & CohortPreviewRowFields,
  activeSlice: ReportPickSliceContext | null | undefined,
  activeAnalysisScope?: ConditionAnalysisScope | null
): CohortTradesResolveRequestBody {
  return buildCohortResolveRequestFromRow(row, activeSlice, activeAnalysisScope);
}

export function buildSituationRowResolveBody(
  row: ConditionAnalysisSituationRow & CohortPreviewRowFields,
  activeSlice: ReportPickSliceContext | null | undefined,
  activeAnalysisScope?: ConditionAnalysisScope | null
): CohortTradesResolveRequestBody {
  return buildCohortResolveRequestFromRow(row, activeSlice, activeAnalysisScope);
}

export function situationTargetFromSituationRow(
  row: ConditionAnalysisSituationRow & CohortPreviewRowFields
): CohortTradesResolveTarget {
  const policy = extractPolicyImportNodes(row);
  return {
    type: "situation",
    situation: situationTargetFromRow(row),
    ...(row.clauses?.length ? { clauses: row.clauses } : {}),
    ...(policy ? { policy_import_nodes: policy } : {}),
  };
}

export function buildConditionAnalysisResolveBody(
  target: CohortTradesResolveTarget,
  sliceContext: CohortTradesApiSliceContext | null
): CohortTradesResolveRequestBody {
  return {
    source: "condition_analysis",
    slice_context: sliceContext ?? null,
    target,
  };
}

export function buildManagementExplorerResolveBody(
  sliceId: string,
  policySource?: CohortPreviewRowFields | null
): CohortTradesResolveRequestBody {
  const target = attachPolicyImportNodesToTarget(
    { type: "slice", slice_id: sliceId },
    policySource
  );
  return {
    source: "management_explorer",
    target,
  };
}

/**
 * Primary Exit Placement preview body — BE looks up `management_explorer.slices`
 * by exact `slice_id` and evaluates that row's gate condition.
 */
export function buildExplorerRowCohortResolveBody(
  row: ManagementExplorerSliceRow
): CohortTradesResolveRequestBody | null {
  const sliceId = buildExplorerSliceId(row);
  if (!sliceId) return null;
  return buildManagementExplorerResolveBody(sliceId);
}

/**
 * Fallback when gate membership resolves empty: same segment via condition_analysis
 * (no situation filter). Matches Regime drilldown membership for the breakdown row.
 */
export function buildExplorerSegmentFallbackResolveBody(
  row: ManagementExplorerSliceRow
): CohortTradesResolveRequestBody | null {
  const segmentKey = String(row.segment_key ?? "").trim();
  const breakdownMap = String(row.breakdown_map ?? "").trim();
  const tableKey = regimeDrilldownTableKeyFromBreakdownMap(breakdownMap);
  if (!segmentKey || !tableKey) return null;
  const target = bucketTargetFromSummaryRow(tableKey, segmentKey);
  if (!target) return null;
  return buildConditionAnalysisResolveBody(
    target,
    sliceContextForRootSummaryTable(tableKey, segmentKey)
  );
}

export function mergeCohortResolvePages(
  pages: CohortTradesResolveResponse[]
): { tradeIds: string[]; total: number } {
  const seen = new Set<string>();
  const tradeIds: string[] = [];
  let total = 0;

  for (const page of pages) {
    if (typeof page.pagination?.total === "number") {
      total = page.pagination.total;
    }
    for (const id of page.trade_ids ?? []) {
      const trimmed = id.trim();
      if (!trimmed || seen.has(trimmed)) continue;
      seen.add(trimmed);
      tradeIds.push(trimmed);
    }
  }

  if (total === 0 && tradeIds.length > 0) {
    total = tradeIds.length;
  }

  return { tradeIds, total };
}

export async function resolveAllCohortTradeIds(
  reportId: string,
  body: CohortTradesResolveRequestBody,
  options?: {
    limit?: number;
    /** Override page fetch (demo job resolve uses poll-cookie auth). */
    resolvePage?: (
      id: string,
      pageBody: CohortTradesResolveRequestBody,
      query?: { offset?: number; limit?: number }
    ) => Promise<CohortTradesResolveResponse>;
  }
): Promise<{ tradeIds: string[]; total: number }> {
  const limit = options?.limit ?? DEFAULT_PAGE_LIMIT;
  const resolvePage = options?.resolvePage ?? resolveCohortTrades;
  const pages: CohortTradesResolveResponse[] = [];
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    const page = await resolvePage(reportId, body, { offset, limit });
    pages.push(page);
    hasMore = Boolean(page.pagination?.has_more);
    offset += limit;
    if (!hasMore) break;
    if (offset > 20_000) break;
  }

  return mergeCohortResolvePages(pages);
}

export function cohortOutcomeFromPnl(netPnl: number | null | undefined): CohortOutcome {
  if (netPnl == null || Number.isNaN(Number(netPnl))) return "breakeven";
  const n = Number(netPnl);
  if (n > 0) return "win";
  if (n < 0) return "loss";
  return "breakeven";
}

export function readTradeNetPnl(trade: {
  net_pnl?: number | null;
  netPnL?: number | null;
  gross_pnl?: number | null;
  grossPnL?: number | null;
}): number | null {
  if (trade.net_pnl != null && !Number.isNaN(Number(trade.net_pnl))) {
    return Number(trade.net_pnl);
  }
  if (trade.netPnL != null && !Number.isNaN(Number(trade.netPnL))) {
    return Number(trade.netPnL);
  }
  if (trade.gross_pnl != null && !Number.isNaN(Number(trade.gross_pnl))) {
    return Number(trade.gross_pnl);
  }
  if (trade.grossPnL != null && !Number.isNaN(Number(trade.grossPnL))) {
    return Number(trade.grossPnL);
  }
  return null;
}

export function cohortOutcomeFromOverlay(trade: {
  net_pnl?: number | null;
  netPnL?: number | null;
  gross_pnl?: number | null;
  grossPnL?: number | null;
  is_win?: boolean;
  is_loss?: boolean;
}): CohortOutcome {
  const pnl = readTradeNetPnl(trade);
  if (pnl != null) return cohortOutcomeFromPnl(pnl);
  if (trade.is_win) return "win";
  if (trade.is_loss) return "loss";
  return "breakeven";
}

function normalizeTradeId(id: string | null | undefined): string {
  return String(id ?? "").trim().toLowerCase();
}

function attachPolicyImportNodesToTarget(
  target: CohortTradesResolveTarget,
  row: CohortPreviewRowFields | null | undefined
): CohortTradesResolveTarget {
  const policy = extractPolicyImportNodes(row);
  if (!policy?.length) return target;
  return { ...target, policy_import_nodes: policy };
}

export function bucketTargetFromConditionSlice(
  slice: ConditionAnalysisSlice
): CohortTradesResolveTarget | null {
  const policySource = slice.node as CohortPreviewRowFields | undefined;
  const scopeKey = slice.node?.analysis_scope?.segment_key?.trim();

  if (scopeKey) {
    const target: CohortTradesResolveTarget = { type: "segment", segment_key: scopeKey };
    return attachPolicyImportNodesToTarget(target, policySource);
  }

  const mc = slice.market_condition;
  const cd = slice.candle_direction;
  const td = slice.direction;
  const session = slice.session_id?.trim();
  const dimensionCount = [mc, cd, td, session].filter(Boolean).length;

  if (mc && dimensionCount === 1) {
    const key = normalizeRegimeValue(mc) ?? mc.trim().toLowerCase();
    return attachPolicyImportNodesToTarget({ type: "segment", key }, policySource);
  }
  if (td && dimensionCount === 1) {
    return attachPolicyImportNodesToTarget({ type: "segment", key: td }, policySource);
  }
  if (cd && dimensionCount === 1) {
    const key = normalizeCandleDirection(cd) ?? cd.trim().toLowerCase();
    return attachPolicyImportNodesToTarget({ type: "segment", key }, policySource);
  }
  if (session && dimensionCount === 1) {
    return attachPolicyImportNodesToTarget(
      { type: "segment", segment_key: session.toLowerCase() },
      policySource
    );
  }

  return null;
}

export function buildConditionSliceResolveBody(
  slice: ConditionAnalysisSlice
): CohortTradesResolveRequestBody | null {
  const target = bucketTargetFromConditionSlice(slice);
  if (!target) return null;
  return buildConditionAnalysisResolveBody(target, sliceContextForConditionSlice(slice));
}

export function filterTradesForCohortPreview<T extends {
  trade_id?: string | null;
  bucket_overlay?: "in_bucket" | "out_of_bucket";
  cohort_outcome?: CohortOutcome;
}>(
  trades: T[],
  filter: CohortListFilter,
  cohortIdSet?: Set<string> | null,
): T[] {
  if (filter === "all") return trades;
  return trades.filter((t) => {
    const n = String(t.trade_id ?? "").trim().toLowerCase();
    const inCohort =
      t.bucket_overlay === "in_bucket" ||
      Boolean(cohortIdSet && n && cohortIdSet.has(n));
    if (!inCohort) return false;
    if (filter === "cohort") return true;
    if (filter === "winners") return t.cohort_outcome === "win";
    if (filter === "losers") return t.cohort_outcome === "loss";
    return true;
  });
}

/** Scope report Trade[] rows to a cohort preview list filter (by resolved trade IDs). */
export function filterReportTradesForCohortPreview<
  T extends {
    id?: string | null;
    trade_id?: string | null;
    net_pnl?: number | null;
    netPnL?: number | null;
    gross_pnl?: number | null;
    grossPnL?: number | null;
  },
>(trades: T[], filter: CohortListFilter, cohortTradeIds: string[]): T[] {
  if (filter === "all" || cohortTradeIds.length === 0) return trades;
  const idSet = new Set(
    cohortTradeIds.map((id) => String(id ?? "").trim().toLowerCase()).filter(Boolean),
  );
  return trades.filter((t) => {
    const a = String(t.id ?? "").trim().toLowerCase();
    const b = String(t.trade_id ?? "").trim().toLowerCase();
    const inCohort = (Boolean(a) && idSet.has(a)) || (Boolean(b) && idSet.has(b));
    if (!inCohort) return false;
    if (filter === "cohort") return true;
    const outcome = cohortOutcomeFromPnl(readTradeNetPnl(t));
    if (filter === "winners") return outcome === "win";
    if (filter === "losers") return outcome === "loss";
    return true;
  });
}

/** Simulator kept/removed overlay — list + chart filters (regime policy veto). */
export type PolicyListFilter =
  | "all"
  | "kept"
  | "removed"
  | "removed_winners"
  | "removed_losers"
  | "changed";

export type PolicyOverlayStats = {
  kept: number;
  keptWins: number;
  keptLosses: number;
  keptWinRate: number | null;
  keptNetPnl: number;
  removed: number;
  removedWins: number;
  removedLosses: number;
  removedBreakeven: number;
  removedWinRate: number | null;
  removedNetPnl: number;
  totalTagged: number;
};

export type OverlaySideStats = {
  count: number;
  wins: number;
  losses: number;
  breakeven: number;
  winRate: number | null;
  netPnl: number;
};

export type OverlayDirectionStats = {
  count: number;
  wins: number;
  losses: number;
  breakeven: number;
  winRate: number | null;
  netPnl: number;
  longs: OverlaySideStats;
  shorts: OverlaySideStats;
};

function emptyOverlaySideStats(): OverlaySideStats {
  return { count: 0, wins: 0, losses: 0, breakeven: 0, winRate: null, netPnl: 0 };
}

function finalizeOverlaySideStats(side: OverlaySideStats): OverlaySideStats {
  const decided = side.wins + side.losses;
  return {
    ...side,
    winRate: decided > 0 ? (side.wins / decided) * 100 : null,
  };
}

/**
 * Aggregate win/loss + long/short stats for a trade set (active overlay filter / affected cohort).
 */
export function computeOverlayDirectionStats(
  trades: Array<{
    direction?: "long" | "short" | string | null;
    net_pnl?: number | null;
    netPnL?: number | null;
    gross_pnl?: number | null;
    grossPnL?: number | null;
    is_win?: boolean;
    is_loss?: boolean;
  }>
): OverlayDirectionStats {
  const longs = emptyOverlaySideStats();
  const shorts = emptyOverlaySideStats();
  let wins = 0;
  let losses = 0;
  let breakeven = 0;
  let netPnl = 0;

  for (const trade of trades) {
    const outcome = cohortOutcomeFromOverlay(trade);
    const pnl = readTradeNetPnl(trade) ?? 0;
    netPnl += pnl;
    if (outcome === "win") wins += 1;
    else if (outcome === "loss") losses += 1;
    else breakeven += 1;

    const side = String(trade.direction ?? "").toLowerCase() === "short" ? shorts : longs;
    side.count += 1;
    side.netPnl += pnl;
    if (outcome === "win") side.wins += 1;
    else if (outcome === "loss") side.losses += 1;
    else side.breakeven += 1;
  }

  const decided = wins + losses;
  return {
    count: trades.length,
    wins,
    losses,
    breakeven,
    winRate: decided > 0 ? (wins / decided) * 100 : null,
    netPnl,
    longs: finalizeOverlaySideStats(longs),
    shorts: finalizeOverlaySideStats(shorts),
  };
}

export function filterTradesForPolicyOverlay<
  T extends {
    policy_overlay?: "kept" | "removed";
    simulation_overlay?: "changed" | "unchanged";
    net_pnl?: number | null;
    netPnL?: number | null;
    gross_pnl?: number | null;
    grossPnL?: number | null;
    is_win?: boolean;
    is_loss?: boolean;
  },
>(trades: T[], filter: PolicyListFilter): T[] {
  if (filter === "all") return trades;
  return trades.filter((t) => {
    if (filter === "kept") return t.policy_overlay === "kept";
    if (filter === "removed") return t.policy_overlay === "removed";
    if (filter === "removed_winners") {
      return t.policy_overlay === "removed" && cohortOutcomeFromOverlay(t) === "win";
    }
    if (filter === "removed_losers") {
      return t.policy_overlay === "removed" && cohortOutcomeFromOverlay(t) === "loss";
    }
    if (filter === "changed") return t.simulation_overlay === "changed";
    return true;
  });
}

/** Exit Placement / TM simulation overlay — list + chart filters. */
export type SimulationListFilter = "all" | "changed" | "unchanged";

export function filterTradesForSimulationOverlay<
  T extends { simulation_overlay?: "changed" | "unchanged" },
>(trades: T[], filter: SimulationListFilter): T[] {
  if (filter === "all") return trades;
  return trades.filter((t) => {
    if (filter === "changed") return t.simulation_overlay === "changed";
    if (filter === "unchanged") return t.simulation_overlay === "unchanged";
    return true;
  });
}

export function computePolicyOverlayStats(
  trades: Array<{
    policy_overlay?: "kept" | "removed";
    net_pnl?: number | null;
    netPnL?: number | null;
    gross_pnl?: number | null;
    grossPnL?: number | null;
    is_win?: boolean;
    is_loss?: boolean;
  }>
): PolicyOverlayStats {
  let kept = 0;
  let keptWins = 0;
  let keptLosses = 0;
  let keptNetPnl = 0;
  let removed = 0;
  let removedWins = 0;
  let removedLosses = 0;
  let removedBreakeven = 0;
  let removedNetPnl = 0;

  for (const trade of trades) {
    const tag = trade.policy_overlay;
    if (tag !== "kept" && tag !== "removed") continue;
    const outcome = cohortOutcomeFromOverlay(trade);
    const pnl = readTradeNetPnl(trade) ?? 0;

    if (tag === "kept") {
      kept += 1;
      if (outcome === "win") keptWins += 1;
      else if (outcome === "loss") keptLosses += 1;
      keptNetPnl += pnl;
      continue;
    }

    removed += 1;
    if (outcome === "win") removedWins += 1;
    else if (outcome === "loss") removedLosses += 1;
    else removedBreakeven += 1;
    removedNetPnl += pnl;
  }

  const removedDecided = removedWins + removedLosses;
  const removedWinRate = removedDecided > 0 ? (removedWins / removedDecided) * 100 : null;
  const keptDecided = keptWins + keptLosses;
  const keptWinRate = keptDecided > 0 ? (keptWins / keptDecided) * 100 : null;

  return {
    kept,
    keptWins,
    keptLosses,
    keptWinRate,
    keptNetPnl,
    removed,
    removedWins,
    removedLosses,
    removedBreakeven,
    removedWinRate,
    removedNetPnl,
    totalTagged: kept + removed,
  };
}

export type CohortPreviewStats = {
  /** Trades from the resolved cohort that matched loaded trade rows. */
  inCohort: number;
  /** Total trade IDs returned by cohort-trades/resolve (before matching). */
  resolvedCount: number;
  wins: number;
  losses: number;
  breakeven: number;
  winRate: number | null;
  netPnl: number;
  longs: OverlaySideStats;
  shorts: OverlaySideStats;
};

type CohortPreviewTradeInput = {
  direction?: "long" | "short" | string | null;
  net_pnl?: number | null;
  netPnL?: number | null;
  gross_pnl?: number | null;
  grossPnL?: number | null;
  is_win?: boolean;
  is_loss?: boolean;
};

export function computeCohortPreviewStats(
  cohortTradeIds: string[],
  tradesById: Map<string, CohortPreviewTradeInput>
): CohortPreviewStats {
  const cohortSet = new Set(cohortTradeIds.map((id) => normalizeTradeId(id)).filter(Boolean));
  const longs = emptyOverlaySideStats();
  const shorts = emptyOverlaySideStats();
  let wins = 0;
  let losses = 0;
  let breakeven = 0;
  let netPnl = 0;
  let matched = 0;

  for (const id of Array.from(cohortSet)) {
    const trade = tradesById.get(id);
    if (!trade) continue;
    matched += 1;
    const pnl = readTradeNetPnl(trade) ?? 0;
    const outcome = cohortOutcomeFromOverlay(trade);
    if (outcome === "win") wins += 1;
    else if (outcome === "loss") losses += 1;
    else breakeven += 1;
    netPnl += pnl;

    const side = String(trade.direction ?? "").toLowerCase() === "short" ? shorts : longs;
    side.count += 1;
    side.netPnl += pnl;
    if (outcome === "win") side.wins += 1;
    else if (outcome === "loss") side.losses += 1;
    else side.breakeven += 1;
  }

  const decided = wins + losses;
  const winRate = decided > 0 ? (wins / decided) * 100 : null;

  return {
    inCohort: matched,
    resolvedCount: cohortSet.size,
    wins,
    losses,
    breakeven,
    winRate,
    netPnl,
    longs: finalizeOverlaySideStats(longs),
    shorts: finalizeOverlaySideStats(shorts),
  };
}

export function computeCohortPreviewStatsFromOverlays(
  cohortTradeIds: string[],
  overlays: Array<
    CohortPreviewTradeInput & {
      trade_id?: string | null;
    }
  >
): CohortPreviewStats {
  const byId = new Map<string, CohortPreviewTradeInput & { trade_id?: string | null }>();
  for (const overlay of overlays) {
    const id = normalizeTradeId(overlay.trade_id);
    if (id) byId.set(id, overlay);
  }
  return computeCohortPreviewStats(cohortTradeIds, byId);
}
